/**
 * @module data/itemTypes
 * @description Zotero item types configuration with fields and labels
 *
 * Based on official Zotero documentation:
 * https://www.zotero.org/support/kb/item_types_and_fields
 *
 * Provides:
 * - Item type definitions with their specific fields
 * - Creator type definitions per item type
 * - Field labels for i18n
 * - Helper functions for labels and defaults
 */

import type { ItemType, ItemTypeConfig, ItemField, CreatorType, ItemData } from '@/types';

// =============================================================================
// ITEM TYPES CONFIGURATION
// Complete list of Zotero item types with their specific fields
// =============================================================================

export const ITEM_TYPES: Record<string, ItemTypeConfig> = {
  // -------------------------------------------------------------------------
  // Books and Publications
  // -------------------------------------------------------------------------
  book: {
    label: 'item.types.book',
    icon: '📚',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'series',
      'seriesNumber',
      'volume',
      'numberOfVolumes',
      'edition',
      'place',
      'publisher',
      'date',
      'numPages',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  },
  bookSection: {
    label: 'item.types.bookSection',
    icon: '📖',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'bookTitle',
      'series',
      'seriesNumber',
      'volume',
      'numberOfVolumes',
      'edition',
      'place',
      'publisher',
      'date',
      'pages',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'bookAuthor', 'contributor', 'editor', 'seriesEditor', 'translator'],
  },

  // -------------------------------------------------------------------------
  // Periodicals (Journals, Magazines, Newspapers)
  // -------------------------------------------------------------------------
  journalArticle: {
    label: 'item.types.journalArticle',
    icon: '📄',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'publicationTitle',
      'volume',
      'issue',
      'pages',
      'date',
      'series',
      'seriesTitle',
      'seriesText',
      'journalAbbreviation',
      'language',
      'DOI',
      'ISSN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'editor', 'reviewedAuthor', 'translator'],
  },
  magazineArticle: {
    label: 'item.types.magazineArticle',
    icon: '📰',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'publicationTitle',
      'volume',
      'issue',
      'date',
      'pages',
      'language',
      'ISSN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'reviewedAuthor', 'translator'],
  },
  newspaperArticle: {
    label: 'item.types.newspaperArticle',
    icon: '🗞️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'publicationTitle',
      'place',
      'edition',
      'date',
      'section',
      'pages',
      'language',
      'ISSN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'reviewedAuthor', 'translator'],
  },

  // -------------------------------------------------------------------------
  // Reference Works
  // -------------------------------------------------------------------------
  encyclopediaArticle: {
    label: 'item.types.encyclopediaArticle',
    icon: '📚',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'encyclopediaTitle',
      'series',
      'seriesNumber',
      'volume',
      'numberOfVolumes',
      'edition',
      'place',
      'publisher',
      'date',
      'pages',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  },
  dictionaryEntry: {
    label: 'item.types.dictionaryEntry',
    icon: '📖',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'dictionaryTitle',
      'series',
      'seriesNumber',
      'volume',
      'numberOfVolumes',
      'edition',
      'place',
      'publisher',
      'date',
      'pages',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  },

  // -------------------------------------------------------------------------
  // Academic and Research
  // -------------------------------------------------------------------------
  thesis: {
    label: 'item.types.thesis',
    icon: '🎓',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'thesisType',
      'university',
      'place',
      'date',
      'numPages',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor'],
  },
  conferencePaper: {
    label: 'item.types.conferencePaper',
    icon: '🎤',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'proceedingsTitle',
      'conferenceName',
      'place',
      'publisher',
      'volume',
      'pages',
      'series',
      'date',
      'DOI',
      'ISBN',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  },
  report: {
    label: 'item.types.report',
    icon: '📋',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'reportNumber',
      'reportType',
      'seriesTitle',
      'place',
      'institution',
      'date',
      'pages',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'seriesEditor', 'translator'],
  },
  presentation: {
    label: 'item.types.presentation',
    icon: '💼',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'presentationType',
      'meetingName',
      'place',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['presenter', 'contributor'],
  },
  dataset: {
    label: 'item.types.dataset',
    icon: '📊',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'identifier',
      'repository',
      'repositoryLocation',
      'format',
      'date',
      'language',
      'DOI',
      'citationKey',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor'],
  },
  standard: {
    label: 'item.types.standard',
    icon: '📐',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'number',
      'organization',
      'committee',
      'type',
      'status',
      'versionNumber',
      'place',
      'publisher',
      'date',
      'numPages',
      'DOI',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor'],
  },

  // -------------------------------------------------------------------------
  // Primary Sources and Archives
  // -------------------------------------------------------------------------
  manuscript: {
    label: 'item.types.manuscript',
    icon: '📜',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'manuscriptType',
      'place',
      'date',
      'numPages',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'translator'],
  },
  letter: {
    label: 'item.types.letter',
    icon: '✉️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'letterType',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'recipient'],
  },
  interview: {
    label: 'item.types.interview',
    icon: '🎙️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'interviewMedium',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['interviewee', 'contributor', 'interviewer', 'translator'],
  },
  email: {
    label: 'item.types.email',
    icon: '📧',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'subject',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'recipient'],
  },
  instantMessage: {
    label: 'item.types.instantMessage',
    icon: '💬',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'recipient'],
  },

  // -------------------------------------------------------------------------
  // Visual Arts and Media
  // -------------------------------------------------------------------------
  artwork: {
    label: 'item.types.artwork',
    icon: '🎨',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'artworkMedium',
      'artworkSize',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['artist', 'contributor'],
  },
  map: {
    label: 'item.types.map',
    icon: '🗺️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'mapType',
      'scale',
      'seriesTitle',
      'edition',
      'place',
      'publisher',
      'date',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['cartographer', 'contributor', 'seriesEditor'],
  },
  film: {
    label: 'item.types.film',
    icon: '🎬',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'distributor',
      'date',
      'genre',
      'videoRecordingFormat',
      'runningTime',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['director', 'contributor', 'producer', 'scriptwriter'],
  },
  videoRecording: {
    label: 'item.types.videoRecording',
    icon: '📹',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'videoRecordingFormat',
      'seriesTitle',
      'volume',
      'numberOfVolumes',
      'place',
      'studio',
      'date',
      'runningTime',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['director', 'contributor', 'castMember', 'producer', 'scriptwriter'],
  },
  audioRecording: {
    label: 'item.types.audioRecording',
    icon: '🎵',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'audioRecordingFormat',
      'seriesTitle',
      'volume',
      'numberOfVolumes',
      'place',
      'label',
      'date',
      'runningTime',
      'language',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['performer', 'contributor', 'composer', 'wordsBy'],
  },

  // -------------------------------------------------------------------------
  // Broadcasts and Podcasts
  // -------------------------------------------------------------------------
  tvBroadcast: {
    label: 'item.types.tvBroadcast',
    icon: '📺',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'programTitle',
      'episodeNumber',
      'videoRecordingFormat',
      'place',
      'network',
      'date',
      'runningTime',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['director', 'contributor', 'castMember', 'guest', 'producer', 'scriptwriter'],
  },
  radioBroadcast: {
    label: 'item.types.radioBroadcast',
    icon: '📻',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'programTitle',
      'episodeNumber',
      'audioRecordingFormat',
      'place',
      'network',
      'date',
      'runningTime',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['director', 'contributor', 'castMember', 'guest', 'producer', 'scriptwriter'],
  },
  podcast: {
    label: 'item.types.podcast',
    icon: '🎧',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'seriesTitle',
      'episodeNumber',
      'audioFileType',
      'date',
      'runningTime',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['podcaster', 'contributor', 'guest'],
  },

  // -------------------------------------------------------------------------
  // Web Content
  // -------------------------------------------------------------------------
  webpage: {
    label: 'item.types.webpage',
    icon: '🌐',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'websiteTitle',
      'websiteType',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'translator'],
  },
  blogPost: {
    label: 'item.types.blogPost',
    icon: '✍️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'blogTitle',
      'websiteType',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'commenter'],
  },
  forumPost: {
    label: 'item.types.forumPost',
    icon: '💭',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'forumTitle',
      'postType',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor'],
  },

  // -------------------------------------------------------------------------
  // Software
  // -------------------------------------------------------------------------
  computerProgram: {
    label: 'item.types.computerProgram',
    icon: '💻',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'seriesTitle',
      'versionNumber',
      'date',
      'system',
      'place',
      'company',
      'programmingLanguage',
      'ISBN',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['programmer', 'contributor'],
  },

  // -------------------------------------------------------------------------
  // Legal Documents
  // -------------------------------------------------------------------------
  bill: {
    label: 'item.types.bill',
    icon: '📜',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'billNumber',
      'code',
      'codeVolume',
      'section',
      'codePages',
      'legislativeBody',
      'session',
      'date',
      'language',
      'history',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['sponsor', 'contributor', 'cosponsor'],
  },
  statute: {
    label: 'item.types.statute',
    icon: '⚖️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'nameOfAct',
      'code',
      'codeNumber',
      'publicLawNumber',
      'dateEnacted',
      'pages',
      'section',
      'session',
      'language',
      'history',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor'],
  },
  hearing: {
    label: 'item.types.hearing',
    icon: '🏛️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'committee',
      'place',
      'publisher',
      'numberOfVolumes',
      'documentNumber',
      'pages',
      'legislativeBody',
      'session',
      'date',
      'language',
      'history',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['contributor'],
  },
  case: {
    label: 'item.types.case',
    icon: '⚖️',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'caseName',
      'court',
      'dateDecided',
      'docketNumber',
      'reporter',
      'reporterVolume',
      'firstPage',
      'language',
      'history',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'counsel'],
  },
  patent: {
    label: 'item.types.patent',
    icon: '💡',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'place',
      'country',
      'assignee',
      'issuingAuthority',
      'patentNumber',
      'filingDate',
      'pages',
      'applicationNumber',
      'priorityNumbers',
      'issueDate',
      'references',
      'legalStatus',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'rights',
      'extra',
    ],
    creatorTypes: ['inventor', 'contributor', 'attorneyAgent'],
  },

  // -------------------------------------------------------------------------
  // Generic / Fallback
  // -------------------------------------------------------------------------
  document: {
    label: 'item.types.document',
    icon: '📄',
    fields: [
      'title',
      'creators',
      'abstractNote',
      'publisher',
      'date',
      'language',
      'shortTitle',
      'url',
      'accessDate',
      'archive',
      'archiveLocation',
      'libraryCatalog',
      'callNumber',
      'rights',
      'extra',
    ],
    creatorTypes: ['author', 'contributor', 'editor', 'reviewedAuthor', 'translator'],
  },
};

// =============================================================================
// FIELD LABELS (i18n keys)
// Complete list of all Zotero fields
// =============================================================================

export const FIELD_LABELS: Record<string, string> = {
  // ----- General Fields -----
  title: 'item.fields.title',
  creators: 'item.fields.creators',
  date: 'item.fields.date',
  abstractNote: 'item.fields.abstractNote',
  language: 'item.fields.language',
  shortTitle: 'item.fields.shortTitle',
  url: 'item.fields.url',
  accessDate: 'item.fields.accessDate',
  rights: 'item.fields.rights',
  extra: 'item.fields.extra',

  // ----- Books and Periodicals -----
  publicationTitle: 'item.fields.publicationTitle',
  bookTitle: 'item.fields.bookTitle',
  dictionaryTitle: 'item.fields.dictionaryTitle',
  encyclopediaTitle: 'item.fields.encyclopediaTitle',
  volume: 'item.fields.volume',
  issue: 'item.fields.issue',
  pages: 'item.fields.pages',
  edition: 'item.fields.edition',
  series: 'item.fields.series',
  seriesNumber: 'item.fields.seriesNumber',
  seriesTitle: 'item.fields.seriesTitle',
  seriesText: 'item.fields.seriesText',
  numberOfVolumes: 'item.fields.numberOfVolumes',
  numPages: 'item.fields.numPages',
  section: 'item.fields.section',
  place: 'item.fields.place',
  publisher: 'item.fields.publisher',
  journalAbbreviation: 'item.fields.journalAbbreviation',
  ISBN: 'item.fields.ISBN',
  ISSN: 'item.fields.ISSN',
  DOI: 'item.fields.DOI',

  // ----- Archives and Access -----
  archive: 'item.fields.archive',
  archiveLocation: 'item.fields.archiveLocation',
  libraryCatalog: 'item.fields.libraryCatalog',
  callNumber: 'item.fields.callNumber',

  // ----- Reports and Theses -----
  reportType: 'item.fields.reportType',
  reportNumber: 'item.fields.reportNumber',
  institution: 'item.fields.institution',
  university: 'item.fields.university',
  thesisType: 'item.fields.thesisType',

  // ----- Presentations and Conferences -----
  proceedingsTitle: 'item.fields.proceedingsTitle',
  conferenceName: 'item.fields.conferenceName',
  meetingName: 'item.fields.meetingName',
  presentationType: 'item.fields.presentationType',

  // ----- Recordings and Broadcasts -----
  audioRecordingFormat: 'item.fields.audioRecordingFormat',
  videoRecordingFormat: 'item.fields.videoRecordingFormat',
  audioFileType: 'item.fields.audioFileType',
  runningTime: 'item.fields.runningTime',
  programTitle: 'item.fields.programTitle',
  episodeNumber: 'item.fields.episodeNumber',
  network: 'item.fields.network',
  label: 'item.fields.label',
  distributor: 'item.fields.distributor',
  studio: 'item.fields.studio',
  genre: 'item.fields.genre',

  // ----- Images, Artwork, and Maps -----
  artworkMedium: 'item.fields.artworkMedium',
  artworkSize: 'item.fields.artworkSize',
  scale: 'item.fields.scale',
  mapType: 'item.fields.mapType',

  // ----- Primary Sources -----
  medium: 'item.fields.medium',
  interviewMedium: 'item.fields.interviewMedium',
  letterType: 'item.fields.letterType',
  manuscriptType: 'item.fields.manuscriptType',
  subject: 'item.fields.subject',

  // ----- Websites -----
  websiteTitle: 'item.fields.websiteTitle',
  websiteType: 'item.fields.websiteType',
  blogTitle: 'item.fields.blogTitle',
  forumTitle: 'item.fields.forumTitle',
  postType: 'item.fields.postType',

  // ----- Software -----
  versionNumber: 'item.fields.versionNumber',
  system: 'item.fields.system',
  company: 'item.fields.company',
  programmingLanguage: 'item.fields.programmingLanguage',

  // ----- Dataset -----
  identifier: 'item.fields.identifier',
  repository: 'item.fields.repository',
  repositoryLocation: 'item.fields.repositoryLocation',
  format: 'item.fields.format',
  citationKey: 'item.fields.citationKey',

  // ----- Standard -----
  number: 'item.fields.number',
  organization: 'item.fields.organization',
  committee: 'item.fields.committee',
  type: 'item.fields.type',
  status: 'item.fields.status',

  // ----- Legal: Legislation -----
  nameOfAct: 'item.fields.nameOfAct',
  billNumber: 'item.fields.billNumber',
  code: 'item.fields.code',
  codeVolume: 'item.fields.codeVolume',
  codeNumber: 'item.fields.codeNumber',
  publicLawNumber: 'item.fields.publicLawNumber',
  dateEnacted: 'item.fields.dateEnacted',
  codePages: 'item.fields.codePages',
  legislativeBody: 'item.fields.legislativeBody',
  session: 'item.fields.session',
  history: 'item.fields.history',
  documentNumber: 'item.fields.documentNumber',

  // ----- Legal: Cases -----
  caseName: 'item.fields.caseName',
  court: 'item.fields.court',
  dateDecided: 'item.fields.dateDecided',
  docketNumber: 'item.fields.docketNumber',
  reporter: 'item.fields.reporter',
  reporterVolume: 'item.fields.reporterVolume',
  firstPage: 'item.fields.firstPage',

  // ----- Legal: Patents -----
  country: 'item.fields.country',
  assignee: 'item.fields.assignee',
  issuingAuthority: 'item.fields.issuingAuthority',
  patentNumber: 'item.fields.patentNumber',
  filingDate: 'item.fields.filingDate',
  issueDate: 'item.fields.issueDate',
  applicationNumber: 'item.fields.applicationNumber',
  priorityNumbers: 'item.fields.priorityNumbers',
  references: 'item.fields.references',
  legalStatus: 'item.fields.legalStatus',

  // ----- Extra fields from CSL -----
  originalDate: 'item.fields.originalDate',
  originalTitle: 'item.fields.originalTitle',
  originalPublisher: 'item.fields.originalPublisher',
  originalPublisherPlace: 'item.fields.originalPublisherPlace',
  PMID: 'item.fields.PMID',
  PMCID: 'item.fields.PMCID',
};

// =============================================================================
// CREATOR TYPES
// Complete list of all Zotero creator types
// =============================================================================

export const CREATOR_TYPES: Record<CreatorType, string> = {
  // Primary creators
  author: 'item.creatorTypes.author',
  editor: 'item.creatorTypes.editor',
  contributor: 'item.creatorTypes.contributor',
  translator: 'item.creatorTypes.translator',
  seriesEditor: 'item.creatorTypes.seriesEditor',
  bookAuthor: 'item.creatorTypes.bookAuthor',
  reviewedAuthor: 'item.creatorTypes.reviewedAuthor',

  // Media creators
  director: 'item.creatorTypes.director',
  producer: 'item.creatorTypes.producer',
  scriptwriter: 'item.creatorTypes.scriptwriter',
  performer: 'item.creatorTypes.performer',
  composer: 'item.creatorTypes.composer',
  wordsBy: 'item.creatorTypes.wordsBy',
  castMember: 'item.creatorTypes.castMember',
  guest: 'item.creatorTypes.guest',
  podcaster: 'item.creatorTypes.podcaster',

  // Visual arts
  artist: 'item.creatorTypes.artist',
  cartographer: 'item.creatorTypes.cartographer',

  // Academic
  presenter: 'item.creatorTypes.presenter',
  programmer: 'item.creatorTypes.programmer',

  // Interview
  interviewee: 'item.creatorTypes.interviewee',
  interviewer: 'item.creatorTypes.interviewer',

  // Communication
  recipient: 'item.creatorTypes.recipient',
  commenter: 'item.creatorTypes.commenter',

  // Legal
  sponsor: 'item.creatorTypes.sponsor',
  cosponsor: 'item.creatorTypes.cosponsor',
  counsel: 'item.creatorTypes.counsel',
  inventor: 'item.creatorTypes.inventor',
  attorneyAgent: 'item.creatorTypes.attorneyAgent',
};

// =============================================================================
// CREATOR TYPES BY ITEM TYPE
// Maps each item type to its valid creator types
// =============================================================================

export const CREATOR_TYPES_BY_ITEM: Record<string, CreatorType[]> = {
  book: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  bookSection: ['author', 'bookAuthor', 'contributor', 'editor', 'seriesEditor', 'translator'],
  journalArticle: ['author', 'contributor', 'editor', 'reviewedAuthor', 'translator'],
  magazineArticle: ['author', 'contributor', 'reviewedAuthor', 'translator'],
  newspaperArticle: ['author', 'contributor', 'reviewedAuthor', 'translator'],
  encyclopediaArticle: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  dictionaryEntry: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  thesis: ['author', 'contributor'],
  conferencePaper: ['author', 'contributor', 'editor', 'seriesEditor', 'translator'],
  report: ['author', 'contributor', 'seriesEditor', 'translator'],
  presentation: ['presenter', 'contributor'],
  dataset: ['author', 'contributor'],
  standard: ['author', 'contributor'],
  manuscript: ['author', 'contributor', 'translator'],
  letter: ['author', 'contributor', 'recipient'],
  interview: ['interviewee', 'contributor', 'interviewer', 'translator'],
  email: ['author', 'contributor', 'recipient'],
  instantMessage: ['author', 'contributor', 'recipient'],
  artwork: ['artist', 'contributor'],
  map: ['cartographer', 'contributor', 'seriesEditor'],
  film: ['director', 'contributor', 'producer', 'scriptwriter'],
  videoRecording: ['director', 'contributor', 'castMember', 'producer', 'scriptwriter'],
  audioRecording: ['performer', 'contributor', 'composer', 'wordsBy'],
  tvBroadcast: ['director', 'contributor', 'castMember', 'guest', 'producer', 'scriptwriter'],
  radioBroadcast: ['director', 'contributor', 'castMember', 'guest', 'producer', 'scriptwriter'],
  podcast: ['podcaster', 'contributor', 'guest'],
  webpage: ['author', 'contributor', 'translator'],
  blogPost: ['author', 'contributor', 'commenter'],
  forumPost: ['author', 'contributor'],
  computerProgram: ['programmer', 'contributor'],
  bill: ['sponsor', 'contributor', 'cosponsor'],
  statute: ['author', 'contributor'],
  hearing: ['contributor'],
  case: ['author', 'contributor', 'counsel'],
  patent: ['inventor', 'contributor', 'attorneyAgent'],
  document: ['author', 'contributor', 'editor', 'reviewedAuthor', 'translator'],
};

// =============================================================================
// SPECIAL COLLECTIONS
// =============================================================================

export interface SpecialCollectionDef {
  key: string;
  nameKey: string;
  icon: string;
  special: true;
}

export const SPECIAL_COLLECTIONS: SpecialCollectionDef[] = [
  { key: 'duplicates', nameKey: 'collection.duplicates', icon: '📑', special: true },
  { key: 'uncategorized', nameKey: 'collection.unfiled', icon: '📄', special: true },
  { key: 'trash', nameKey: 'collection.trash', icon: '🗑️', special: true },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get item type configuration
 */
export function getItemTypeConfig(itemType: string): ItemTypeConfig {
  return ITEM_TYPES[itemType] || ITEM_TYPES.document;
}

/**
 * Get field label i18n key
 */
export function getFieldLabelKey(field: string): string {
  return FIELD_LABELS[field] || `item.fields.${field}`;
}

/**
 * Get creator type label i18n key
 */
export function getCreatorTypeLabelKey(creatorType: CreatorType): string {
  return CREATOR_TYPES[creatorType] || `item.creatorTypes.${creatorType}`;
}

/**
 * Check if a collection key is a special collection
 */
export function isSpecialCollection(key: string): boolean {
  return SPECIAL_COLLECTIONS.some((c) => c.key === key);
}

/**
 * Get all available item types for select dropdown
 */
export function getAvailableItemTypes(): Array<{ value: string; labelKey: string; icon: string }> {
  return Object.entries(ITEM_TYPES).map(([key, config]) => ({
    value: key,
    labelKey: config.label,
    icon: config.icon,
  }));
}

/**
 * Get all available creator types for select dropdown
 */
export function getAvailableCreatorTypes(): Array<{ value: CreatorType; labelKey: string }> {
  return (Object.entries(CREATOR_TYPES) as [CreatorType, string][]).map(([key, labelKey]) => ({
    value: key,
    labelKey,
  }));
}

/**
 * Get valid creator types for a specific item type
 */
export function getCreatorTypesForItemType(itemType: string): CreatorType[] {
  return CREATOR_TYPES_BY_ITEM[itemType] || CREATOR_TYPES_BY_ITEM.document;
}

/**
 * Get the default creator type for a specific item type
 */
export function getDefaultCreatorType(itemType: string): CreatorType {
  const validTypes = getCreatorTypesForItemType(itemType);
  return validTypes[0] || 'author';
}

/**
 * Get fields for a specific item type
 */
export function getFieldsForItemType(itemType: string): ItemField[] {
  const config = getItemTypeConfig(itemType);
  return config.fields;
}

/**
 * Create default item data for a given type
 */
export function createDefaultItem(itemType: ItemType = 'book'): Partial<ItemData> {
  const defaultCreatorType = getDefaultCreatorType(itemType);

  return {
    itemType,
    title: '',
    creators: [{ creatorType: defaultCreatorType, firstName: '', lastName: '' }],
    date: '',
    language: '',
    abstractNote: '',
    url: '',
    accessDate: '',
    rights: '',
    extra: '',
  };
}
